import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import SectionPicker from '@components/picker/SectionPicker'
import TextInput from '@components/textInput/TextInput'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useDebounce from '@utils/useDebounce'

import sanitize from '@utils/Sanitizer'

interface Props {
  activities: FamilleResponse[]
  selected: string[] // ids
  onActivitiesSelected: (items: string[]) => void
  style?: 'default' | 'list'
}

const ActivityFilter = ({ activities, selected, onActivitiesSelected, style = 'default' }: Props) => {
  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [local, setLocal] = React.useState(selected)
  const [search, debounced, setSearch] = useDebounce('')

  const data = React.useMemo(
    () => activities.filter((p) => p.parentIdFamille && p.enabled && sanitize(p.libelle).includes(sanitize(debounced))),
    [activities, debounced]
  )

  const familles = React.useMemo(
    () =>
      activities
        .filter((p) => !p.parentIdFamille)
        .filter((p) => p.enabled)
        .sort((a, b) => (a.isRemiseEnEtat ? 1 : -1)),
    [activities]
  )

  const sections = React.useMemo(
    () =>
      familles.map((f) => ({
        title: f.libelle,
        data: data
          .filter((p) => p.parentIdFamille === f.id)
          .map((p) => ({ label: p.libelle, value: p.id }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      })),
    [familles, data]
  )

  React.useEffect(() => {
    setLocal(selected)
  }, [selected])

  const onItemSelected = (item: string) => {
    const isSelected = local.includes(item)
    const next = isSelected ? local.filter((id) => id !== item) : [...local, item]

    if (style === 'list') {
      onActivitiesSelected(next)
    } else {
      setLocal(next)
    }
  }

  const onSectionSelected = (values: string[], clear: boolean) => {
    const next = clear ? local.filter((id) => !values.includes(id)) : Array.from(new Set([...local, ...values]))

    if (style === 'list') {
      onActivitiesSelected(next)
    } else {
      setLocal(next)
    }
  }

  return (
    <>
      <SearchContainer>
        <TextInput
          onChange={setSearch}
          searchStyle={bp === 'phone' ? 'onLight' : 'onDark'}
          left={{ name: 'search' }}
          placeholder={i18n.t('common.search')}
          value={search}
        />
      </SearchContainer>
      <FilterView>
        <SectionPicker
          data={sections}
          onItemSelected={onItemSelected}
          onSectionSelected={onSectionSelected}
          selected={local}
          background={style === 'default' ? theme.colors.background : theme.colors.backgroundSecondary}
        />
      </FilterView>

      {style === 'default' && (
        <Footer>
          {local.length > 0 ? (
            <TouchableOpacity activeOpacity={0.9} onPress={() => setLocal([])}>
              <Text>{i18n.t('common.erase')}</Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          <Button label={i18n.t('common.save')} onClick={() => onActivitiesSelected(local)} status="active" />
        </Footer>
      )}
    </>
  )
}

export default ActivityFilter

const SearchContainer = styled(View)`
  padding: 16px 16px 0;
`

const FilterView = styled(View)`
  flex: 1;
  padding: 31px 22px 31px 31px;
`

const Footer = styled(View)`
  border-top-width: 1px;
  border-top-color: ${(props) => props.theme.colors.abandoned};
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 16px 31px;
`
